import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Descriptions, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  deductionApi,
  type DeductionBatchDetail,
  type DeductionBatchDetailItem,
  type DeductionBatchOutcome,
  type DeductionItemResult,
} from '../../api/deduction.api';
import { EllipsisText } from '../../components/EllipsisCell';
import { DetailDrawer } from '../../components/DetailDrawer';

const { Text } = Typography;

const outcomeLabel: Record<DeductionBatchOutcome, { text: string; color: string }> = {
  ALL_SUCCESS: { text: '全部成功', color: 'success' },
  PARTIAL_FAILURE: { text: '部分失败', color: 'warning' },
  ALL_FAILURE: { text: '全部失败', color: 'error' },
};

const itemResultLabel: Record<DeductionItemResult, { text: string; color: string }> = {
  DEDUCTED: { text: '成功扣减', color: 'success' },
  SKIPPED_NO_BALANCE: { text: '余额不足', color: 'error' },
  SKIPPED_ACCOUNT_INVALID: { text: '账户不可用', color: 'error' },
  REVERSED: { text: '已冲正', color: 'default' },
  // O-14：期限卡覆盖（quantity 恒 0、计入成功人数、无冲正入口）
  PERIOD_COVERED: { text: '期限卡覆盖', color: 'blue' },
};

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm');
}

function OutcomeTag({ result }: { result: DeductionBatchOutcome }) {
  const label = outcomeLabel[result];
  return <Tag color={label.color}>{label.text}</Tag>;
}

function ItemResultTag({ result }: { result: DeductionItemResult }) {
  const label = itemResultLabel[result];
  return <Tag color={label.color}>{label.text}</Tag>;
}

export interface DeductionDetailDrawerProps {
  batchId: string;
  onClose: () => void;
}

/** ADM-22 消课批次只读详情抽屉。 */
export function DeductionDetailDrawer({ batchId, onClose }: DeductionDetailDrawerProps) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<DeductionBatchDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await deductionApi.getBatch(batchId));
    } catch (error) {
      message.error(error instanceof Error ? error.message : '消课批次详情加载失败');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const columns: ColumnsType<DeductionBatchDetailItem> = [
    { title: '学生', dataIndex: 'studentName', width: 100 },
    {
      title: '课时类别',
      dataIndex: 'deductLessonKind',
      width: 90,
      render: (value: DeductionBatchDetailItem['deductLessonKind'], record) =>
        // O-14：期限卡覆盖行不扣池，扣减类别显示「期限卡覆盖」
        record.result === 'PERIOD_COVERED'
          ? '期限卡覆盖'
          : value
            ? (value === 'GIFTED' ? '赠送课' : '正式课')
            : '-',
    },
    {
      title: '扣减数量',
      dataIndex: 'quantity',
      width: 90,
      align: 'center',
      // O-14：覆盖行数量恒 0
      render: (value: number, record) => (record.result === 'PERIOD_COVERED' ? 0 : value),
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 100,
      render: (value: DeductionItemResult) => <ItemResultTag result={value} />,
    },
    {
      title: '失败原因',
      dataIndex: 'failReason',
      width: 180,
      render: (value: string | null) => value ?? '-',
    },
    {
      title: '流水号',
      dataIndex: 'ledgerEntryId',
      width: 180,
      ellipsis: true,
      // O-14：覆盖行流水号为 USAGE 流水号
      render: (value: string | null) => <EllipsisText value={value} />,
    },
  ];

  return (
    <DetailDrawer
      open
      width={900}
      loading={loading}
      title={`消课批次详情 · ${batchId}`}
      onClose={onClose}
      destroyOnHidden
      data-testid="deduction-detail-drawer"
    >
      {detail && (
        <>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="批次号">
              <EllipsisText value={detail.id} />
            </Descriptions.Item>
            <Descriptions.Item label="批次结果">
              <OutcomeTag result={detail.result} />
            </Descriptions.Item>
            <Descriptions.Item label="课次">
              {detail.sessionDate} {dayjs(detail.startAt).format('HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="班级">{detail.className}</Descriptions.Item>
            <Descriptions.Item label="校区">{detail.campusName || '—'}</Descriptions.Item>
            <Descriptions.Item label="课程 SKU">{detail.skuName}</Descriptions.Item>
            <Descriptions.Item label="教师">{detail.teacherName}</Descriptions.Item>
            <Descriptions.Item label="应消人数">{detail.expectedCount}</Descriptions.Item>
            <Descriptions.Item label="成功人数">{detail.successCount}</Descriptions.Item>
            <Descriptions.Item label="失败人数">{detail.failedCount}</Descriptions.Item>
            <Descriptions.Item label="操作员工">{detail.operatorName}</Descriptions.Item>
            <Descriptions.Item label="操作时间">
              {formatDateTime(detail.createdAt)}
            </Descriptions.Item>
            {detail.reason && (
              <Descriptions.Item label="补消原因" span={2}>
                {detail.reason}
              </Descriptions.Item>
            )}
          </Descriptions>
          <Space style={{ marginTop: 16 }}>
            <Button
              size="small"
              data-testid="go-exceptions"
              onClick={() => {
                onClose();
                navigate(`/deduction/exceptions?sessionId=${detail.sessionId}`);
              }}
            >
              去处理
            </Button>
            <Button
              size="small"
              data-testid="go-ledger"
              onClick={() => {
                onClose();
                navigate('/deduction/ledger');
              }}
            >
              看账本
            </Button>
          </Space>
          <Text strong style={{ display: 'block', margin: '20px 0 12px' }}>
            学生结果明细
          </Text>
          <Table
            size="small"
            rowKey="studentId"
            columns={columns}
            dataSource={detail.items}
            pagination={false}
            scroll={{ x: 740 }}
            data-testid="deduction-detail-table"
          />
        </>
      )}
    </DetailDrawer>
  );
}

export { OutcomeTag };
